import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './TeamMain.module.css';
import { Edit, Delete, Visibility, ContentCopy, Add, ArrowForward, ArrowBack, KeyboardArrowDown, Close } from '@mui/icons-material';
import AddTeamModal from './AddTeamModal';
import SuccessModal from './SuccessModal';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import TeamDeleteModal from './TeamDeleteModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditTeamModal from './EditTeamModal';

const TeamMain = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [passwordType, setPasswordType] = useState(''); // 'auto' or 'manual'
  const [manualPassword, setManualPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(true);
  const user = Auth.getUser();
  const userId = user?.id;
  const workspaceId = Auth.getWorkspaceId();
  const token = window.localStorage.getItem('token');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deletingUserName, setDeletingUserName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [editedUserEmail, setEditedUserEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/user/get-all-users`, {
        params: { workspaceId, userId, page: currentPage },
        headers: { Authorization: `Bearer ${token}` }
      });
      const mapped = response.data.users.map(u => {
        const firstName = u.firstName || '';
        const lastName = u.lastName || '';
        const fullName = `${firstName} ${lastName}`.replace(/ +/g, ' ').trim();
        const name = `${firstName.split(' ')[0]} ${lastName.split(' ')[0]}`.trim();
        const initials = (firstName.charAt(0) || '') + (lastName.charAt(0) || '');
        let userType = 'USER';
        if (u.superAdmin) userType = 'SUPER-ADMIN';
        else if (u.admin) userType = 'ACCOUNT-ADMIN';
        return {
          id: u.id,
          initials,
          name,
          fullName,
          firstName,
          lastName,
          email: u.email || '—',
          phone: u.phoneNumber || '—',
          userType,
          profileImage: u.profileImage?.url || null
        };
      });
      setTeamMembers(mapped);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workspaceId && userId) {
      fetchUsers();
    }
  }, [workspaceId, userId, currentPage, token]);

  const handlePrev = () => currentPage > 1 && setCurrentPage(p => p - 1);
  const handleNext = () => currentPage < totalPages && setCurrentPage(p => p + 1);

 
  const closeSuccessModal = () => { 
    setIsSuccessModalOpen(false);
    setNewUserEmail('');
    setGeneratedPassword('');
  };

  const handleSubmit = () => {
    // Simulate successful user creation
    const email = document.getElementById('email').value;
    setNewUserEmail(email);
    setIsModalOpen(false);
    setIsSuccessModalOpen(true);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'email') {
      toast.success('Email copied to clipboard!');
    } else if (type === 'password') {
      toast.success('Password copied to clipboard!');
    } else {
      toast.success('Copied to clipboard!');
    }
  };

  const copyCredentialsTogether = () => {
    const credentials = `user: ${newUserEmail}\npassword: ${generatedPassword}`;
    navigator.clipboard.writeText(credentials);
    toast.success('User credentials copied to clipboard!');
  };

  const handleDeleteClick = (userId, userName) => {
    setDeletingUserId(userId);
    setDeletingUserName(userName);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const workspaceId = Auth.getWorkspaceId();
      const userId = Auth.getUser()?.id;
      await axios({
        method: 'delete',
        url: `${API_URL}/user?workspaceId=${workspaceId}&userId=${userId}`,
        data: { id: deletingUserId, workspaceId, userId },
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success('User deleted successfully');
      setIsDeleteModalOpen(false);
      setDeletingUserId(null);
      setDeletingUserName('');
      // Refresh the users list using the existing fetchUsers function
      await fetchUsers();
    } catch (err) {
      toast.error('Error deleting user');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles.container}>

      {/* Team list container */}
      <div className={styles.listContainer}>
        <div className={styles.header}>
          <div className={styles.headerName}>Name</div>
          <div className={styles.headerEmail}>Email</div>
          <div className={styles.headerPhone}>Phone</div>
          <div className={styles.headerUserType}>User Type</div>
          <div className={styles.headerAction}>Action</div>
        </div>

        <div className={styles.members}>
          {loading ? (
            // Loading skeleton
            Array.from({ length: 5 }).map((_, index) => (
              <div key={`skeleton-${index}`} className={styles.memberRow} style={{ opacity: 0.6, pointerEvents: 'none' }}>
                <div className={styles.name}>
                  <div className={styles.avatar} style={{ backgroundColor: '#e5e7eb', width: 40, height: 40, borderRadius: '50%', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                  <div style={{ width: 120, height: 16, backgroundColor: '#e5e7eb', borderRadius: 4, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                </div>
                <div className={styles.email}>
                  <div style={{ width: 180, height: 16, backgroundColor: '#e5e7eb', borderRadius: 4, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                </div>
                <div className={styles.phone} style={{ width: 120, height: 16, backgroundColor: '#e5e7eb', borderRadius: 4, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                <div className={styles.userType} style={{ width: 100, height: 16, backgroundColor: '#e5e7eb', borderRadius: 4, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                <div className={styles.actions} style={{ width: 100, height: 16, backgroundColor: '#e5e7eb', borderRadius: 4, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
              </div>
            ))
          ) : (
            teamMembers.map((member) => (
            <div key={member.id} className={styles.memberRow}>
              <div className={styles.name}>
                <div className={styles.avatar} style={{ backgroundColor: member.initials === 'GD' ? '#3b82f6' : (member.initials === 'MW' ? '#06b6d4' : '#d97706'), overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {member.profileImage ? (
                    <img src={member.profileImage} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : (
                    member.initials
                  )}
                </div>
                <span>{member.name}</span>
              </div>
              <div className={styles.email}>
                <div className={styles.emailInfo}>
                  <span>{member.email}</span>
                </div>
                <ContentCopy className={styles.copyIcon} onClick={() => copyToClipboard(member.email, 'email')} style={{ cursor: 'pointer' }} />
              </div>
              <div className={styles.phone}>{member.phone}</div>
              <div className={styles.userType}>{member.userType}</div>
              <div className={styles.actions}>
                <Edit className={styles.actionIcon} onClick={() => { setEditUser(member); setIsEditModalOpen(true); }} />
                <Delete className={styles.actionIcon} onClick={() => handleDeleteClick(member.id, member.name)} />
                <Visibility className={styles.actionIcon} onClick={() => { setSelectedUser(member); setIsViewModalOpen(true); }} />
              </div>
            </div>
          ))
          )}
        </div>

        <div className={styles.pagination}>
          <span>Page {currentPage} / {totalPages}</span>
          <div className={styles.paginationControls}>
            <button
                className={`${styles.pageButton} ${currentPage === 1 ? styles.pageButtonDisabled : ''}`}
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
              <ArrowBack fontSize="small" /> Previous
            </button>
            <button className={styles.pageNumber}>{currentPage}</button>
              <button
                className={`${styles.pageButton} ${currentPage === totalPages ? styles.pageButtonDisabled : ''}`}
                onClick={handleNext}
                disabled={currentPage === totalPages}
            >
              Next <ArrowForward fontSize="small" />
            </button>
          </div>
        </div>

        {/* Add Team Button */}
        <div className={styles.addTeamSection}>
          <button className={styles.addTeamButton} onClick={() => setIsModalOpen(true)}>
            <Add fontSize="small" /> Add Team
          </button>
        </div>
      </div>

      {/* Add Team Modal */}
      <AddTeamModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={({email, password}) => {
          setNewUserEmail(email);
          setGeneratedPassword(password);
          setIsModalOpen(false);
          setIsSuccessModalOpen(true);
          fetchUsers(); // Refresh the user list after creating a new user
        }}
        passwordType={passwordType}
        setPasswordType={setPasswordType}
        manualPassword={manualPassword}
        setManualPassword={setManualPassword}
        generatedPassword={generatedPassword}
        setGeneratedPassword={setGeneratedPassword}
      />

      {/* Success Modal */}
      <SuccessModal 
        isOpen={isSuccessModalOpen}
        onClose={closeSuccessModal}
        newUserEmail={newUserEmail}
        generatedPassword={generatedPassword}
        onCopyEmail={() => copyToClipboard(newUserEmail, 'email')}
        onCopyPassword={() => copyToClipboard(generatedPassword, 'password')}
        onCopyCredentials={copyCredentialsTogether}
      />

      <TeamDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        userName={deletingUserName}
      />

      {isViewModalOpen && selectedUser && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent} style={{ maxWidth: 400, borderRadius: 20, padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button className={styles.closeButton} onClick={() => setIsViewModalOpen(false)} style={{ position: 'absolute', top: 20, right: 20 }}><Close /></button>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '0 0 20px 0' }}>
        {selectedUser.profileImage ? (
          <img src={selectedUser.profileImage} alt={selectedUser.name} style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' }} />
        ) : (
          <div style={{ width: 96, height: 96, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #e2e8f0' }}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="#e5e7eb" />
              <path d="M24 24c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7 3.582 7 8 7zm0 4c-5.33 0-16 2.686-16 8v4h32v-4c0-5.314-10.67-8-16-8z" fill="#bdbdbd" />
            </svg>
          </div>
        )}
      </div>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: '#222', lineHeight: 1.25, wordBreak: 'break-word', marginBottom: 2, whiteSpace: 'pre-line' }}>{selectedUser.fullName || selectedUser.name}</div>
        <div style={{ fontSize: 18, color: '#6b7280', marginTop: 4 }}>{selectedUser.email}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '24px 0', width: '100%' }}>
        <div style={{ display: 'flex', width: '100%', maxWidth: 320, justifyContent: 'center' }}>
          <div style={{ flex: 1, textAlign: 'right', color: '#222', fontWeight: 500, fontSize: 16, lineHeight: '2.2', paddingRight: 16 }}>
            <div>Phone</div>
            <div>User Type</div>
          </div>
          <div style={{ flex: 2, textAlign: 'left', color: '#222', fontSize: 16, lineHeight: '2.2' }}>
            <div>{selectedUser.phone}</div>
            <div>{selectedUser.userType}</div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, width: '100%' }}>
        <button onClick={() => setIsViewModalOpen(false)} style={{ padding: '10px 32px', borderRadius: 8, background: '#572af1', border: 'none', fontSize: 16, color: '#fff', fontWeight: 500, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'background 0.2s' }}
  onMouseOver={e => e.currentTarget.style.background = '#291b3b'}
  onMouseOut={e => e.currentTarget.style.background = '#572af1'}
>
  Close
</button>
      </div>
    </div>
  </div>
)}

      <EditTeamModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={editUser ? { ...editUser, workspaceId, adminUserId: userId, fullName: editUser.fullName } : null}
        loading={editLoading}
        onSubmit={async (data) => {
          setEditLoading(true);
          try {
            const original = editUser;
            const changedFields = {};
            const alwaysFields = ['targetUserId', 'workspaceId', 'adminUserId'];
            let passwordChanged = false;
            Object.keys(data).forEach(key => {
              if (alwaysFields.includes(key)) {
                changedFields[key] = data[key];
              } else if (data[key] !== undefined && data[key] !== original[key]) {
                changedFields[key] = data[key];
                if (key === 'password') passwordChanged = true;
              }
            });
            await axios.post(`${API_URL}/user/admin-user`, changedFields);
            toast.success('User updated successfully!');
            if (passwordChanged && changedFields.password) {
              setNewPassword(changedFields.password);
              setEditedUserEmail(data.email);
              setShowPasswordModal(true);
            }
            setIsEditModalOpen(false);
            setTimeout(() => setEditUser(null), 300);
            fetchUsers();
          } catch (err) {
            toast.error('Error updating user');
          } finally {
            setEditLoading(false);
          }
        }}
      />
      <SuccessModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        generatedPassword={newPassword}
        newUserEmail={editedUserEmail}
        onCopyPassword={() => { navigator.clipboard.writeText(newPassword); toast.success('Password copied!'); }}
        onCopyEmail={() => { navigator.clipboard.writeText(editedUserEmail); toast.success('Email copied!'); }}
        onCopyCredentials={() => { navigator.clipboard.writeText(`${editedUserEmail}\n${newPassword}`); toast.success('Credentials copied!'); }}
      />
    </div>
  );
};

export default TeamMain;